import React, { useState, useMemo, useCallback } from 'react'
import { Target, Clock, Users, Trophy, Star, Check, Lock, Zap } from 'lucide-react'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'

// ================================================================================
// TYPES
// ================================================================================
interface WeeklyChallenge {
  id: string
  title: string
  description: string
  icon: string
  points: number
  progress: number
  total: number
  deadline: string
  participants: number
  category: 'awareness' | 'social' | 'health' | 'community'
  joined: boolean
}

interface DailyTask {
  id: string
  title: string
  description: string
  points: number
  completed: boolean
}

// ================================================================================
// MOCK DATA
// ================================================================================
const mockChallenges: WeeklyChallenge[] = [
  {
    id: '1',
    title: 'TB Knowledge Champion',
    description: 'Complete all TB awareness quizzes this week',
    icon: '📚',
    points: 500,
    progress: 3,
    total: 5,
    deadline: '2026-06-09T23:59:59',
    participants: 234,
    category: 'awareness',
    joined: true,
  },
  {
    id: '2',
    title: 'Community Connector',
    description: 'Invite 5 friends to join TB awareness groups',
    icon: '👥',
    points: 300,
    progress: 2,
    total: 5,
    deadline: '2026-06-09T23:59:59',
    participants: 156,
    category: 'social',
    joined: true,
  },
  {
    id: '3',
    title: 'Health Tracker',
    description: 'Log your daily health activities for 7 days',
    icon: '💪',
    points: 400,
    progress: 4,
    total: 7,
    deadline: '2026-06-09T23:59:59',
    participants: 189,
    category: 'health',
    joined: true,
  },
  {
    id: '4',
    title: 'Story Sharer',
    description: 'Share 3 stories about TB awareness',
    icon: '📖',
    points: 250,
    progress: 1,
    total: 3,
    deadline: '2026-06-09T23:59:59',
    participants: 98,
    category: 'community',
    joined: false,
  },
  {
    id: '5',
    title: 'Live Educator',
    description: 'Host a live session about TB prevention',
    icon: '🎥',
    points: 600,
    progress: 0,
    total: 1,
    deadline: '2026-06-09T23:59:59',
    participants: 45,
    category: 'awareness',
    joined: false,
  },
]

const mockDailyTasks: DailyTask[] = [
  { id: '1', title: 'Read today\'s TB fact', description: 'Learn something new about TB', points: 10, completed: true },
  { id: '2', title: 'Complete daily quiz', description: 'Test your knowledge', points: 20, completed: true },
  { id: '3', title: 'Share a post', description: 'Spread awareness to your network', points: 15, completed: false },
  { id: '4', title: 'Engage with 3 posts', description: 'Like, comment, or share', points: 10, completed: false },
  { id: '5', title: 'Check your streak', description: 'Maintain your daily streak', points: 5, completed: false },
]

// ================================================================================
// CATEGORY COLORS
// ================================================================================
const CATEGORY_COLORS: Record<string, string> = {
  awareness: 'from-blue-400 to-blue-600',
  social: 'from-purple-400 to-purple-600',
  health: 'from-green-400 to-green-600',
  community: 'from-orange-400 to-orange-600',
}

// ================================================================================
// PROGRESS BAR COMPONENT
// ================================================================================
const ChallengeProgressBar = React.memo(({ value, color = 'primary' }: { value: number; color?: 'primary' | 'success' }) => {
  const barColor = color === 'success' ? 'bg-emerald-500' : 'bg-blue-600'
  return (
    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
      <div
        className={`h-full ${barColor} transition-all duration-300`}
        style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      />
    </div>
  )
})

// ================================================================================
// STAT CARD COMPONENT
// ================================================================================
interface StatCardProps {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: string | number
  gradient: string
}

const StatCard = React.memo(({ icon: Icon, label, value, gradient }: StatCardProps) => (
  <GovCard className="p-4">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-lg font-bold text-gray-900">{value}</p>
      </div>
    </div>
  </GovCard>
))

// ================================================================================
// CHALLENGE CARD COMPONENT
// ================================================================================
interface ChallengeCardProps {
  challenge: WeeklyChallenge
  onJoin: (id: string) => void
}

const ChallengeCard = React.memo(({ challenge, onJoin }: ChallengeCardProps) => {
  const progressPercent = (challenge.progress / challenge.total) * 100
  const isComplete = challenge.progress >= challenge.total
  const deadline = new Date(challenge.deadline)
  const daysLeft = Math.ceil((deadline.getTime() - Date.now()) / (1000 * 60 * 60 * 24))

  return (
    <GovCard className={`p-4 h-full ${isComplete ? 'border-2 border-emerald-500' : ''}`}>
      <div className="flex items-start gap-3">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl bg-gradient-to-br ${CATEGORY_COLORS[challenge.category]}`}>
          {challenge.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <h3 className="font-semibold text-gray-900 truncate">{challenge.title}</h3>
            {isComplete && (
              <span className="px-2 py-0.5 bg-emerald-500 text-white text-xs font-medium rounded-full flex-shrink-0">
                Complete
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mb-2 line-clamp-2">{challenge.description}</p>

          <div className="flex items-center gap-3 mb-2 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <Star className="w-3.5 h-3.5 text-yellow-500" />
              {challenge.points} pts
            </span>
            <span className="flex items-center gap-1">
              <Users className="w-3.5 h-3.5" />
              {challenge.participants}
            </span>
          </div>

          <div className="mb-2">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Progress</span>
              <span>{challenge.progress} / {challenge.total}</span>
            </div>
            <ChallengeProgressBar value={progressPercent} color={isComplete ? 'success' : 'primary'} />
          </div>

          <div className="flex items-center justify-between">
            {!isComplete && challenge.joined && (
              <span className="flex items-center gap-1 text-xs text-orange-500">
                <Clock className="w-3.5 h-3.5" />
                {daysLeft} days left
              </span>
            )}
            {!challenge.joined && !isComplete && (
              <GovButton
                variant="primary"
                size="sm"
                onClick={() => onJoin(challenge.id)}
                className="flex items-center gap-1"
              >
                <Zap className="w-3.5 h-3.5" />
                Join Challenge
              </GovButton>
            )}
            {isComplete && (
              <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                <Check className="w-3.5 h-3.5" />
                Completed
              </span>
            )}
          </div>
        </div>
      </div>
    </GovCard>
  )
})

// ================================================================================
// DAILY TASK COMPONENT
// ================================================================================
interface DailyTaskItemProps {
  task: DailyTask
}

const DailyTaskItem = React.memo(({ task }: DailyTaskItemProps) => (
  <GovCard className={`p-3 flex items-center gap-3 ${task.completed ? 'bg-emerald-50 border-emerald-200' : ''}`}>
    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${task.completed ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-400'}`}>
      {task.completed ? <Check className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
    </div>
    <div className="flex-1 min-w-0">
      <h4 className={`font-medium text-sm ${task.completed ? 'text-emerald-700' : 'text-gray-900'}`}>
        {task.title}
      </h4>
      <p className="text-xs text-gray-500 truncate">{task.description}</p>
    </div>
    <span className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${task.completed ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
      +{task.points}
    </span>
  </GovCard>
))

// ================================================================================
// COMPLETED CHALLENGE COMPONENT
// ================================================================================
interface CompletedChallengeProps {
  challenge: WeeklyChallenge
}

const CompletedChallenge = React.memo(({ challenge }: CompletedChallengeProps) => (
  <GovCard className="p-4 border-2 border-emerald-500 bg-emerald-50">
    <div className="flex items-center gap-3">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl bg-gradient-to-br ${CATEGORY_COLORS[challenge.category]}`}>
        {challenge.icon}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-gray-900">{challenge.title}</h3>
          <span className="px-2 py-0.5 bg-emerald-500 text-white text-xs font-medium rounded-full">
            Complete
          </span>
        </div>
        <p className="text-sm text-gray-500">{challenge.description}</p>
        <div className="flex items-center gap-1 mt-1">
          <Star className="w-4 h-4 text-yellow-500" />
          <span className="text-sm font-medium text-emerald-600">+{challenge.points} points earned</span>
        </div>
      </div>
      <Trophy className="w-8 h-8 text-emerald-500 flex-shrink-0" />
    </div>
  </GovCard>
))

// ================================================================================
// MAIN PAGE COMPONENT
// ================================================================================
const ChallengesPage = () => {
  const [activeTab, setActiveTab] = useState<'weekly' | 'daily' | 'completed'>('weekly')
  const [challenges, setChallenges] = useState<WeeklyChallenge[]>(mockChallenges)

  // Memoized computed values
  const completedChallenges = useMemo(() => challenges.filter(c => c.progress >= c.total), [challenges])
  const totalWeeklyPoints = useMemo(() => challenges.reduce((sum, c) => sum + c.points, 0), [challenges])
  const earnedWeeklyPoints = useMemo(
    () => challenges.filter(c => c.progress >= c.total).reduce((sum, c) => sum + c.points, 0),
    [challenges]
  )
  const completedDailyTasks = useMemo(() => mockDailyTasks.filter(t => t.completed).length, [])
  const totalDailyPoints = useMemo(() => mockDailyTasks.reduce((sum, t) => sum + t.points, 0), [])
  const earnedDailyPoints = useMemo(() => mockDailyTasks.filter(t => t.completed).reduce((sum, t) => sum + t.points, 0), [])

  // Handlers
  const handleJoinChallenge = useCallback((id: string) => {
    setChallenges(prev => prev.map(c => c.id === id ? { ...c, joined: true } : c))
  }, [])

  const stats = useMemo(() => [
    { icon: Target, label: 'Active Challenges', value: challenges.length, gradient: 'from-emerald-400 to-teal-500' },
    { icon: Trophy, label: 'Completed', value: completedChallenges.length, gradient: 'from-yellow-400 to-orange-500' },
    { icon: Star, label: 'Weekly Points', value: `${earnedWeeklyPoints}/${totalWeeklyPoints}`, gradient: 'from-blue-400 to-blue-600' },
    { icon: Clock, label: 'Daily Tasks', value: `${completedDailyTasks}/${mockDailyTasks.length}`, gradient: 'from-purple-400 to-purple-600' },
  ], [challenges.length, completedChallenges.length, earnedWeeklyPoints, totalWeeklyPoints, completedDailyTasks])

  return (
    <GovLayout>
      <GovPageTitle title="Challenges" subtitle="Complete challenges to earn points and unlock rewards" icon={Target} />

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
        {(['weekly', 'daily', 'completed'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === tab
                ? 'bg-emerald-500 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
            }`}
          >
            {tab === 'weekly' && 'Weekly Challenges'}
            {tab === 'daily' && 'Daily Tasks'}
            {tab === 'completed' && 'Completed'}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'weekly' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {challenges.map(challenge => (
            <ChallengeCard
              key={challenge.id}
              challenge={challenge}
              onJoin={handleJoinChallenge}
            />
          ))}
        </div>
      )}

      {activeTab === 'daily' && (
        <div className="space-y-2">
          {mockDailyTasks.map(task => (
            <DailyTaskItem key={task.id} task={task} />
          ))}

          {/* Daily Progress Summary */}
          <div className="mt-4 p-4 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl text-white">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-white/80 text-xs">Daily Progress</p>
                <p className="text-xl font-bold">{completedDailyTasks} / {mockDailyTasks.length} tasks</p>
              </div>
              <div className="text-right">
                <p className="text-white/80 text-xs">Points Earned</p>
                <p className="text-xl font-bold">{earnedDailyPoints} / {totalDailyPoints}</p>
              </div>
            </div>
            <ChallengeProgressBar value={(completedDailyTasks / mockDailyTasks.length) * 100} color="success" />
          </div>
        </div>
      )}

      {activeTab === 'completed' && (
        <div className="space-y-3">
          {completedChallenges.length === 0 ? (
            <GovEmptyState
              icon="🏆"
              title="No completed challenges yet"
              description="Keep working on your challenges to earn rewards!"
            />
          ) : (
            completedChallenges.map(challenge => (
              <CompletedChallenge key={challenge.id} challenge={challenge} />
            ))
          )}
        </div>
      )}
    </GovLayout>
  )
}

export default React.memo(ChallengesPage)